import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import AiVisibilityLanding from './AiVisibilityLanding';
import { hasAiVisibility, hasTodo } from '../config/aiVisibilityBrands';
import { useAuth } from '../context/AuthContext';
import { Loader2, ExternalLink, ChevronRight, CalendarDays, Layers, ListChecks, Download, Info, Search, Sparkles } from 'lucide-react';
import {
  API, PLATFORM_LABEL, PLATFORM_ORDER, CATEGORY_LABEL, SOURCE_LABEL,
  Card, Seg, PeriodPicker, presetRanges, pct,
} from './YatasAiVisibility';

/**
 * AI Visibility · Yapılacaklar (2 Kas 2026, Yataş talebi).
 * "Bu soruda çıkmıyorsun, bu konu şu kadar aranıyor, sitende içeriği yok → yapılmalı."
 * İki kaynak: ölçtüğümüz sorular (AI cevaplarında markamız önerildi mi, dönem bazlı canlı)
 * + Keyword Planner konu keşfi (ör. "4 saat uyku kuralı"). Backend: GET /api/ai-visibility/todo.
 * Yalnız panel; paylaşım linkinde yok.
 */

const PRIORITY = {
  mutlaka: { label: 'Mutlaka yapılmalı', chip: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500' },
  onerilir: { label: 'Önerilir', chip: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-500' },
  guclendir: { label: 'İçerik var, güçlendir', chip: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'bg-blue-500' },
  tamam: { label: 'Durum iyi', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
};
const CONTENT = {
  var: { label: 'Var', cls: 'text-emerald-700 bg-emerald-50' },
  kismi: { label: 'Kısmen', cls: 'text-amber-700 bg-amber-50' },
  yok: { label: 'Yok', cls: 'text-rose-700 bg-rose-50' },
};
const nf = (v) => (v == null ? '–' : Number(v).toLocaleString('tr-TR'));
const shortUrl = (u) => { try { const x = new URL(u); return x.hostname.replace(/^www\./, '') + decodeURIComponent(x.pathname).replace(/\/$/, ''); } catch { return u; } };

function PriorityChip({ p }) {
  const d = PRIORITY[p];
  return <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2 py-0.5 ring-1 whitespace-nowrap ${d.chip}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${d.dot}`} />{d.label}</span>;
}

function AiCell({ it, own }) {
  if (it.source === 'kesif') return <span className="text-xs text-gray-400">Ölçülmedi</span>;
  const a = it.ai;
  if (!a?.responses) return <span className="text-xs text-gray-400">Bu dönemde cevap yok</span>;
  const tone = a.rate < 0.25 ? 'text-rose-700' : a.rate < 0.5 ? 'text-amber-700' : 'text-emerald-700';
  return (
    <div className="min-w-0">
      <div className={`text-sm font-semibold ${tone}`}>{a.ownRec}/{a.responses} cevapta</div>
      <div className="text-[11px] text-gray-400">{own} önerildi ({pct(a.rate)})</div>
      {a.competitors.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {a.competitors.slice(0, 3).map((c) => (
            <span key={c.name} className="text-[11px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 whitespace-nowrap">{c.name} · {c.n}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function ContentCell({ it }) {
  const c = CONTENT[it.content.status] || CONTENT.yok;
  const first = (it.content.urls || [])[0];
  return (
    <div className="min-w-0">
      <span className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 ${c.cls}`}>{c.label}</span>
      {first && (
        <a href={first.url} target="_blank" rel="noreferrer" className="mt-1 flex items-start gap-1 text-[11px] text-blue-700 hover:underline min-w-0">
          <ExternalLink className="w-3 h-3 mt-0.5 shrink-0" /><span className="truncate">{first.title || shortUrl(first.url)}</span>
        </a>
      )}
    </div>
  );
}

function Row({ it, own }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-gray-100 align-top hover:bg-gray-50/60 cursor-pointer" onClick={() => setOpen(!open)}>
        <td className="py-3 pl-1 pr-2 w-5"><ChevronRight className={`w-4 h-4 text-gray-400 transition ${open ? 'rotate-90' : ''}`} /></td>
        <td className="py-3 pr-4 min-w-[220px]">
          <div className="font-medium text-gray-800">{it.topic}</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-400">
            {it.source === 'soru'
              ? <><Sparkles className="w-3 h-3" />Ölçülen soru{it.category ? ` · ${CATEGORY_LABEL[it.category] || it.category}` : ''}</>
              : <><Search className="w-3 h-3" />Arama verisinden</>}
          </div>
        </td>
        <td className="py-3 pr-4 text-right whitespace-nowrap">
          <div className="text-sm font-semibold text-gray-800">{nf(it.volume)}</div>
          <div className="text-[11px] text-gray-400">{it.source === 'kesif' ? 'konu toplamı' : 'aylık arama'}</div>
        </td>
        <td className="py-3 pr-4 min-w-[170px]"><AiCell it={it} own={own} /></td>
        <td className="py-3 pr-4 min-w-[150px] max-w-[220px]"><ContentCell it={it} /></td>
        <td className="py-3 pr-4"><PriorityChip p={it.priority} /></td>
        <td className="py-3 pr-1 text-sm text-gray-700 min-w-[260px] max-w-[380px]">{it.action}</td>
      </tr>
      {open && (
        <tr className="border-b border-gray-100 bg-gray-50/40">
          <td />
          <td colSpan={6} className="py-4 pr-4">
            <div className="grid md:grid-cols-3 gap-5 text-xs">
              <div>
                {it.promptText && <><div className="font-semibold text-gray-700 mb-1">Yapay zekaya sorulan soru</div><p className="text-gray-600 mb-3">“{it.promptText}”</p></>}
                <div className="font-semibold text-gray-700 mb-1">Arama ifadeleri (Google, aylık)</div>
                <ul className="space-y-0.5">
                  {(it.volumeDetail || []).slice(0, 10).map((v) => (
                    <li key={v.keyword} className={`flex justify-between gap-3 ${v.dup ? 'text-gray-400' : 'text-gray-600'}`}
                      title={v.dup ? 'Google bu ifadeye bir üstteki yakın varyantla aynı sayıyı veriyor; toplama bir kez katıldı.' : undefined}>
                      <span>{v.keyword}{v.dup ? ' · yakın varyant' : ''}</span><span className="tabular-nums">{nf(v.avg)}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-semibold text-gray-700 mb-1">Sitedeki içerik</div>
                {it.content.note && <p className="text-gray-500 mb-1.5">{it.content.note}</p>}
                {(it.content.urls || []).length === 0 && <p className="text-gray-400">Uygun sayfa bulunamadı.</p>}
                {(it.content.urls || []).map((u) => (
                  <a key={u.url} href={u.url} target="_blank" rel="noreferrer" className="flex items-start gap-1.5 text-blue-700 hover:underline mb-1">
                    <ExternalLink className="w-3 h-3 mt-0.5 shrink-0" /><span className="min-w-0"><span className="block truncate">{u.title || shortUrl(u.url)}</span>
                      <span className="block text-gray-400 truncate">{u.kind === 'category' ? 'Kategori sayfası · ' : ''}{shortUrl(u.url)}</span></span>
                  </a>
                ))}
                {it.suggestedTitle && <><div className="font-semibold text-gray-700 mt-3 mb-1">Önerilen başlık</div><p className="text-gray-600">{it.suggestedTitle}</p></>}
              </div>
              <div>
                {it.ai?.responses > 0 ? (
                  <>
                    <div className="font-semibold text-gray-700 mb-1">Bu soruda önerilen markalar</div>
                    {it.ai.competitors.length === 0 && <p className="text-gray-400 mb-2">Başka marka önerilmedi.</p>}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {it.ai.competitors.map((c) => <span key={c.name} className="bg-white ring-1 ring-gray-200 text-gray-700 rounded-full px-2 py-0.5">{c.name} · {c.n} cevap</span>)}
                    </div>
                    <div className="font-semibold text-gray-700 mb-1">Yapay zekanın kaynak gösterdiği siteler</div>
                    {it.ai.sources.length === 0 && <p className="text-gray-400">Kaynak gösterilmedi.</p>}
                    <ul className="space-y-0.5">
                      {it.ai.sources.map((s) => (
                        <li key={s.domain} className="flex justify-between gap-3 text-gray-600">
                          <span className="truncate">{s.label || s.domain} <span className="text-gray-400">· {SOURCE_LABEL[s.type] || s.type}</span></span>
                          <span className="tabular-nums shrink-0">{s.n} cevap</span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-gray-400">{it.source === 'kesif' ? 'Bu konu yapay zeka ölçümünde soru olarak yok; arama hacmi ve site içeriğine göre listelendi.' : 'Seçili dönemde bu soruya ölçülmüş cevap yok.'}</p>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AiVisibilityTodo() {
  const { selectedBrand, user } = useAuth();
  const brandId = selectedBrand?.id;
  const allowed = hasAiVisibility(brandId, user?.email);
  const enabled = hasTodo(brandId, user?.email);
  const defaultPeriod = () => { const p = presetRanges()[1]; return { from: p.from, to: p.to }; }; // Son 30 gün
  const [period, setPeriod] = useState(defaultPeriod);
  const [platform, setPlatform] = useState(null);
  const [world, setWorld] = useState(null);
  const [prio, setPrio] = useState('acik'); // acik = mutlaka+onerilir+guclendir
  const [source, setSource] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      Object.entries({ from: period?.from, to: period?.to, platform, world }).forEach(([k, v]) => { if (v != null && v !== '') p.set(k, v); });
      const res = await fetch(`${API}/api/ai-visibility/todo?${p}`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setData(j);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [enabled, brandId, period, platform, world]);

  useEffect(() => { setWorld(null); }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => (data?.items || []).filter((i) =>
    (prio === 'hepsi' || (prio === 'acik' ? i.priority !== 'tamam' : i.priority === prio)) && (!source || i.source === source)), [data, prio, source]);
  const own = (data?.ownBrands || []).join(' / ');

  const availablePlatforms = useMemo(() => {
    const set = new Set();
    for (const r of data?.runs || []) for (const e of r.config?.platforms || []) set.add(e.platform);
    return PLATFORM_ORDER.filter((p) => set.has(p));
  }, [data]);

  const exportXlsx = () => {
    const rows = items.map((i) => ({
      'Öncelik': PRIORITY[i.priority].label,
      'Konu': i.topic,
      'Kaynak': i.source === 'soru' ? 'Ölçülen soru' : 'Arama verisinden',
      'Yapay zekaya sorulan soru': i.promptText || '',
      'Aylık arama': i.volume ?? '',
      'Arama ifadeleri': (i.volumeDetail || []).map((v) => `${v.keyword} (${v.avg ?? '–'})`).join(', '),
      'AI cevap sayısı': i.ai?.responses ?? '',
      [`${own} önerildi`]: i.ai?.responses ? i.ai.ownRec : '',
      'Önerilen diğer markalar': (i.ai?.competitors || []).map((c) => `${c.name} (${c.n})`).join(', '),
      'Kaynak gösterilen siteler': (i.ai?.sources || []).map((s) => `${s.domain} (${s.n})`).join(', '),
      'Sitede içerik': CONTENT[i.content.status]?.label || '',
      'İçerik adresi': (i.content.urls || []).map((u) => u.url).join(' '),
      'Önerilen başlık': i.suggestedTitle || '',
      'Aksiyon': i.action || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [18, 40, 16, 50, 12, 50, 10, 10, 40, 40, 10, 50, 50, 70].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Yapılacaklar');
    const wl = (data?.worlds || []).find((w) => w.key === data?.world)?.label || 'AI Visibility';
    XLSX.writeFile(wb, `Yapilacaklar_${wl.replace(/\s+/g, '_')}_${period?.from || ''}_${period?.to || ''}.xlsx`);
  };

  if (!allowed) return <AiVisibilityLanding />;
  if (!enabled) {
    return <div className="p-6 max-w-3xl mx-auto"><Card icon={ListChecks} title="Yapılacaklar" foot={false}><p className="text-sm text-gray-500">Bu marka için yapılacaklar listesi henüz hazırlanmadı.</p></Card></div>;
  }

  const c = data?.counts || {};
  const sel = data?.selection;
  return (
    <div className="min-h-screen" lang="tr">
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">AI Visibility · Yapılacaklar</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Yapay zekanın markamızı önermediği sorular ve Google'da aranan ama sitede karşılığı olmayan konular. Her madde için aylık arama, sitedeki mevcut içerik ve yapılması gereken iş.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm px-4 py-3 flex flex-wrap items-center gap-2">
          {(data?.worlds || []).length > 0 && (
            <>
              <div className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 pl-3 pr-1 py-1">
                <Layers className="w-4 h-4 text-blue-600" />
                <select value={data.world || ''} onChange={(e) => setWorld(e.target.value)}
                  className="text-sm font-semibold text-blue-800 bg-transparent border-0 focus:ring-0 pr-8 py-1">
                  {data.worlds.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
                </select>
              </div>
              <div className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
            </>
          )}
          <CalendarDays className="w-4 h-4 text-gray-400" />
          <PeriodPicker value={period?.from ? period : (sel?.from ? { from: sel.from, to: sel.to } : null)} onChange={(v) => setPeriod(v)} />
          <div className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
          <Seg value={platform} onChange={setPlatform}
            options={[{ value: null, label: 'Tümü' }, ...availablePlatforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))]} />
          {loading && data && <Loader2 className="animate-spin text-gray-400 ml-auto" size={16} />}
        </div>

        {error && <div className="rounded-2xl bg-rose-50 text-rose-700 text-sm px-4 py-3">{error}</div>}
        {!data && loading && <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" /></div>}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {['mutlaka', 'onerilir', 'guclendir', 'tamam'].map((k) => (
                <button key={k} onClick={() => setPrio(prio === k ? 'acik' : k)}
                  className={`text-left bg-white rounded-3xl border shadow-sm p-5 transition ${prio === k ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200/80 hover:border-gray-300'}`}>
                  <div className="flex items-center gap-2 text-sm text-gray-500"><span className={`w-2 h-2 rounded-full ${PRIORITY[k].dot}`} />{PRIORITY[k].label}</div>
                  <div className="text-3xl font-semibold text-gray-900 mt-2">{c[k] ?? 0}</div>
                  <div className="text-[11px] text-gray-400 mt-1">
                    {(data.items || []).filter((i) => i.priority === k && i.source === 'soru').length} ölçülen soru · {(data.items || []).filter((i) => i.priority === k && i.source === 'kesif').length} arama konusu
                  </div>
                </button>
              ))}
            </div>

            <Card icon={ListChecks} title="Yapılacaklar listesi"
              sub={`${items.length} madde · öncelik sırasıyla, aynı öncelikte aylık aramaya göre`}>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Seg value={source} onChange={setSource} options={[{ value: null, label: 'Tümü' }, { value: 'soru', label: 'Ölçülen sorular' }, { value: 'kesif', label: 'Arama verisinden' }]} />
                  <select value={prio} onChange={(e) => setPrio(e.target.value)} className="text-sm border border-gray-200/80 rounded-xl pl-3 pr-8 py-2 bg-white shadow-sm font-medium text-gray-700">
                    <option value="acik">Yapılacaklar</option>
                    <option value="mutlaka">Mutlaka yapılmalı</option>
                    <option value="onerilir">Önerilir</option>
                    <option value="guclendir">İçerik var, güçlendir</option>
                    <option value="tamam">Durum iyi</option>
                    <option value="hepsi">Hepsi</option>
                  </select>
                  <button onClick={exportXlsx} disabled={!items.length}
                    className="inline-flex items-center gap-1.5 text-sm font-medium rounded-xl border border-gray-200/80 bg-white shadow-sm px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    <Download className="w-4 h-4" />Excel
                  </button>
                </div>
              {items.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">Bu filtrede madde yok.</p>
              ) : (
                <div className="overflow-x-auto -mx-2 px-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-200">
                        <th className="pb-2" />
                        <th className="pb-2 pr-4 font-medium">Konu</th>
                        <th className="pb-2 pr-4 font-medium text-right">Arama</th>
                        <th className="pb-2 pr-4 font-medium">Yapay zekada</th>
                        <th className="pb-2 pr-4 font-medium">Sitede içerik</th>
                        <th className="pb-2 pr-4 font-medium">Öncelik</th>
                        <th className="pb-2 pr-1 font-medium">Yapılacak</th>
                      </tr>
                    </thead>
                    <tbody>{items.map((it) => <Row key={it.id} it={it} own={own} />)}</tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card icon={Info} tone="gray" title="Nasıl hazırlandı">
              <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-600">
                <div className="space-y-2">
                  <p><b className="text-gray-800">Ölçülen sorular:</b> AI Visibility'de yapay zekaya sorduğumuz sorular. Seçili dönemde bu sorulara gelen cevaplarda {own || 'markamız'} kaç cevapta önerildi, yerine kimler önerildi ve hangi siteler kaynak gösterildi.</p>
                  <p><b className="text-gray-800">Arama verisinden:</b> Google Keyword Planner'da {own ? 'bu dünyanın' : 'markanın'} alanında aranan, bilgi amaçlı konular (ör. uyku kuralları, seçim rehberleri). Ürün ve marka aramaları dahil değil. Bu konular yapay zekaya sorulmadı.</p>
                  <p><b className="text-gray-800">Aylık arama:</b> Türkiye, Google Arama, son 12 ay ortalaması. Ölçülen sorularda en çok aranan ifade, arama konularında konudaki ifadelerin toplamı (Google'ın aynı sayıyı verdiği yakın varyantlar bir kez sayılır).</p>
                  <p><b className="text-gray-800">Sitede içerik:</b> {own} sitelerindeki blog, bilgi ve kategori sayfaları tarandı. "Kısmen" = konuya değinen ama soruyu tam cevaplamayan bir sayfa ya da yalnız ürün listesi var.</p>
                </div>
                <div className="space-y-2">
                  <p><b className="text-gray-800">Mutlaka yapılmalı:</b> yapay zeka cevapların %{Math.round((data.thresholds?.aiGap || 0.25) * 100)}'inden azında bizi öneriyor, sitede içerik yok ve ayda en az 500 arama var (içerik kısmen varsa 2.000). Arama konularında: konuya ayrılmış sayfa yok ve ayda en az 1.000 arama (konuya değinen genel bir yazı varsa 5.000).</p>
                  <p><b className="text-gray-800">Önerilir:</b> yapay zekada çıkmıyoruz ve içerik eksik ama hacim daha düşük; ya da aranan bir konuda sitede içerik yok.</p>
                  <p><b className="text-gray-800">İçerik var, güçlendir:</b> sayfa var ama yapay zeka bizi cevapların yarısından azında öneriyor (ya da sayfa konuyu kısmen işliyor).</p>
                  <p className="text-gray-400">Liste son güncelleme: {data.build?.finishedAt ? new Date(data.build.finishedAt).toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : '–'}. Yapay zeka sütunu seçili döneme göre anında hesaplanır.</p>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
